import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SectionHeading from '../components/ui/SectionHeading';
import SafeIcon from '../common/SafeIcon';
import Newsletter from '../components/ui/Newsletter';
import ROICalculator from '../components/ui/ROICalculator';
import FAQSection from '../components/ui/FAQSection';
import { useFormSubmit } from '../hooks/useFormSubmit';

const getDownloadUrl = (resource) => {
  if (!resource || !resource.url) return '#';
  try {
    const urlObj = new URL(resource.url);
    let filename = resource.title;

    const pathname = urlObj.pathname;
    const extensionMatch = pathname.match(/\.[0-9a-z]+$/i);
    const ext = extensionMatch ? extensionMatch[0] : '';

    if (ext && !filename.toLowerCase().endsWith(ext.toLowerCase())) {
      filename += ext;
    }

    urlObj.searchParams.set('download', filename);
    return urlObj.toString();
  } catch (e) {
    return resource.url;
  }
};

const Resources = () => {
  const [filter, setFilter] = useState('All');
  const [selectedResource, setSelectedResource] = useState(null);
  const [downloadForm, setDownloadForm] = useState({ fullName: '', email: '' });
  const [resourcesList, setResourcesList] = useState([]);
  const { status: downloadStatus, error, submitForm, resetFormStatus: setDownloadStatus } = useFormSubmit();

  const categories = ['All', 'Strategy', 'AI', 'Operations', 'Interactive'];

  useEffect(() => {
    // Dynamic fetch from SEVEN HATS workspace
    const SUPABASE_URL = 'https://zvpxmxcznimhwhxjnhvn.supabase.co';
    const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
    const WORKSPACE_ID = '814a59d1-950a-4588-bd3d-0ae12834b473';

    if (ANON_KEY) {
      fetch(`${SUPABASE_URL}/rest/v1/ad_creatives?workspace_id=eq.${WORKSPACE_ID}&status=eq.published&order=created_at.desc`, {
        headers: {
          'apikey': ANON_KEY,
          'Authorization': `Bearer ${ANON_KEY}`
        }
      })
      .then(res => res.json())
      .then(assets => {
        if (Array.isArray(assets)) {
          const dynamicResources = assets.map(asset => ({
            id: asset.id || `dyn-${Math.random()}`,
            title: asset.name,
            description: asset.type === 'document' ? 'Downloadable strategic document.' : 'Downloadable media asset.',
            type: asset.type || 'Asset',
            category: 'Interactive', // Defaulting to Interactive or Strategy
            icon: 'FiDownload',
            isNew: true,
            url: asset.image_url
          }));
          setResourcesList(dynamicResources);
        }
      })
      .catch(err => console.error("Failed to fetch workspace assets:", err));
    }
  }, []);

  const handleDownloadRequest = async (e) => {
    e.preventDefault();

    const payload = {
      fullName: downloadForm.fullName,
      email: downloadForm.email,
      resourceId: selectedResource?.id,
      resourceTitle: selectedResource?.title,
      listName: 'Resource Download'
    };

    await submitForm(payload);
  };

  const closeDialog = () => {
    setSelectedResource(null);
    setDownloadStatus();
    setDownloadForm({ fullName: '', email: '' });
  };

  const filteredResources = filter === 'All' 
    ? resourcesList
    : resourcesList.filter(r => r.category === filter);

  return (
    <div className="pt-24 min-h-screen bg-white dark:bg-brand-dark transition-colors duration-300 relative overflow-hidden">
      {/* Dark Grid Background */}
      <div 
        className="absolute top-0 left-0 w-full h-[600px] opacity-[0.15] dark:opacity-[0.25] pointer-events-none" 
        style={{ 
          backgroundImage: 'linear-gradient(to right, #40E0D0 1px, transparent 1px), linear-gradient(to bottom, #40E0D0 1px, transparent 1px)', 
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(circle at center, transparent 0%, black 85%)',
          WebkitMaskImage: 'radial-gradient(circle at center, transparent 0%, black 85%)'
        }}
      ></div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 relative z-10">
        <SectionHeading title="Strategic Assets" subtitle="Executive Resources" className="relative z-10" />
        
        <ROICalculator />

        <FAQSection />

        {/* MarTech Toolbox Promotional Banner */}
        <div className="mb-16 relative z-10 bg-brand-dark dark:bg-brand-darker border border-brand-yellow/30 rounded-2xl p-8 md:p-12 overflow-hidden flex flex-col md:flex-row items-center justify-between gap-8 shadow-2xl">
          <div className="absolute top-0 right-0 opacity-5 pointer-events-none">
            <SafeIcon name="Tool" className="w-64 h-64 -mt-10 -mr-10 text-brand-yellow" />
          </div>
          <div className="relative z-10 max-w-2xl text-center md:text-left">
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-yellow block mb-2">Premium Resource</span>
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">The Complete MarTech Toolbox</h3>
            <p className="text-slate-300 font-medium text-sm leading-relaxed">
              Stop guessing about your marketing technology stack. Get access to the Automated Pipeline Grader, MarTech Stack Mapper, and a robust System of Record to diagnose the $80K Tool Trap™ and regain control.
            </p>
          </div>
          <div className="relative z-10 shrink-0">
            <Link to="/martech" className="inline-flex items-center gap-2 px-8 py-4 bg-brand-red text-white text-sm uppercase font-bold tracking-widest hover:bg-brand-yellow hover:text-brand-dark transition-all rounded shadow-lg shadow-brand-red/20">
              <SafeIcon name="Tool" className="w-4 h-4" /> Get The Toolbox
            </Link>
          </div>
        </div>

        <div className="mb-8">
          <h3 className="text-2xl font-heading font-bold text-brand-dark dark:text-white mb-6">Resource Library</h3>
        </div>

        {/* Filter Navigation */}
        <div className="flex flex-wrap gap-4 mb-12 relative z-10">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-6 py-2 rounded-full text-xs uppercase tracking-[0.2em] font-bold transition-all ${
                filter === cat 
                  ? 'bg-brand-dark dark:bg-brand-yellow text-brand-yellow dark:text-brand-dark' 
                  : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative z-10">
          <AnimatePresence mode="popLayout">
            {filteredResources.map((resource) => (
              <motion.div
                key={resource.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="p-8 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 rounded-2xl hover:border-brand-cyan hover:shadow-2xl transition-all duration-300 group relative"
              >
                {resource.isNew && (
                  <span className="absolute top-4 right-4 bg-brand-red text-white text-[9px] uppercase tracking-widest px-3 py-1 font-bold rounded-full shadow-sm">New</span>
                )}
                <div className="w-14 h-14 bg-slate-50 dark:bg-brand-dark rounded-xl flex items-center justify-center mb-6 text-brand-dark dark:text-brand-cyan group-hover:bg-brand-cyan group-hover:text-brand-dark transition-colors">
                  <SafeIcon name={resource.icon.replace('Fi', '')} className="w-7 h-7" />
                </div>
                <span className="text-[10px] uppercase tracking-widest text-slate-400 dark:text-slate-500 font-bold block mb-2">{resource.type}</span>
                <h3 className="text-xl font-heading font-bold text-brand-dark dark:text-white mb-4">{resource.title}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 font-medium leading-relaxed mb-8">
                  {resource.description}
                </p>
                <button
                  onClick={() => setSelectedResource(resource)}
                  className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-brand-dark dark:text-brand-cyan hover:text-brand-cyan dark:hover:text-white transition-all mt-auto"
                >
                  Request Immediate Access <SafeIcon name="Download" className="w-4 h-4" />
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
      <Newsletter />

      {/* Resource Download Modal */}
      <AnimatePresence>
        {selectedResource && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              className="bg-white dark:bg-brand-dark max-w-lg w-full rounded-2xl p-8 shadow-2xl border border-slate-200 dark:border-slate-800 relative"
            >
              <button
                onClick={closeDialog}
                className="absolute top-4 right-4 text-slate-400 hover:text-brand-red transition-colors"
              >
                <SafeIcon name="X" className="w-6 h-6" />
              </button>

              <div className="mb-6">
                <span className="text-xs uppercase tracking-widest text-brand-cyan font-bold block mb-2">Resource Access</span>
                <h3 className="text-2xl font-heading font-bold text-brand-dark dark:text-white mb-2">{selectedResource.title}</h3>
                <p className="text-slate-500 text-sm">Choose how you'd like to access this strategic resource.</p>
              </div>

              {downloadStatus === 'success' ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-brand-cyan/20 text-brand-cyan rounded-full flex items-center justify-center mx-auto mb-4">
                    <SafeIcon name="Check" className="w-8 h-8" />
                  </div>
                  <h4 className="text-lg font-bold text-brand-dark dark:text-white mb-2">Access Granted</h4>
                  <p className="text-sm text-slate-500 mb-6">Thank you. You can now download your strategic resource directly.</p>
                  <div className="flex justify-center gap-4">
                    <a href={getDownloadUrl(selectedResource)} target="_blank" rel="noopener noreferrer" className="px-6 py-3 bg-brand-red text-white text-xs uppercase font-bold tracking-widest rounded hover:bg-red-600 transition-colors inline-flex items-center gap-2">
                      <SafeIcon name="Download" className="w-4 h-4" /> Download Asset
                    </a>
                    <button onClick={closeDialog} className="px-6 py-3 bg-slate-100 dark:bg-slate-800 text-xs uppercase font-bold tracking-widest rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">Close</button>
                  </div>
                </div>
              ) : (
                <div className="space-y-8">
                  {downloadStatus === 'error' && (
                    <div className="p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-500 text-sm font-medium mb-4">
                      {error || 'Failed to submit. Please try again.'}
                    </div>
                  )}
                  <form onSubmit={handleDownloadRequest} className="space-y-4">
                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Full Name</label>
                      <input
                        type="text"
                        name="fullName"
                        required
                        value={downloadForm.fullName}
                        onChange={(e) => setDownloadForm(p => ({...p, fullName: e.target.value}))}
                        className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-4 py-3 focus:border-brand-cyan outline-none rounded-lg text-sm dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Executive Email</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={downloadForm.email}
                        onChange={(e) => setDownloadForm(p => ({...p, email: e.target.value}))}
                        className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-4 py-3 focus:border-brand-cyan outline-none rounded-lg text-sm dark:text-white"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={downloadStatus === 'loading'}
                      className="w-full py-3 bg-brand-red text-white text-xs uppercase tracking-widest font-bold hover:bg-red-600 transition-colors rounded-lg disabled:opacity-50"
                    >
                      {downloadStatus === 'loading' ? 'Processing...' : 'Instant Download'}
                    </button>
                  </form>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-slate-200 dark:border-slate-800"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white dark:bg-brand-dark text-slate-500 text-[10px] uppercase tracking-widest font-bold">Or</span>
                    </div>
                  </div>

                  <div className="text-center">
                    <p className="text-sm text-slate-500 mb-4">Prefer not to provide contact info?</p>
                    <a
                      href="https://www.paypal.com/ncp/payment/KV3WF3XB9LGHA"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setDownloadStatus('success')}
                      className="inline-flex items-center justify-center w-full gap-2 py-3 bg-slate-100 dark:bg-slate-800 text-brand-dark dark:text-white text-xs uppercase tracking-widest font-bold hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors rounded-lg"
                    >
                      Donate Any Amount to Download Directly
                    </a>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Resources;